import { addCommenter, initCommentCounts, fetchDisqus, setCurrentThread } from "../redux/commenting/actions";
import { SmoothScrollToTarget, LoadIntersectionObserver } from "../tfs-utilities";

const Commenting = {
  data: {
    itemAttribute: "data-commentable-item",
    targetAttribute: "data-target",
    disqusClass: "js-disqus",
    disqusId: "disqus_thread",
    countAttribute: "data-commentable-count",
    permalinkAttribute: "data-commentable-permalink",
    disqusPermalinkAttribute: "data-disqus-permalink",
    animationClass: "tfs-do-pulse",
    activeClass: "active"
  },

  init(store) {
    Commenting.data.store = store;

    Commenting.buildState();
    Commenting.initCommentCounts();
    Commenting.listenForButtonClick();
    Commenting.listenForStateChange();
  },

  initCommentCounts() {
    // update the redux store with the initialized disqus data
    return Commenting.data.store.dispatch(initCommentCounts());
  },

  buildState() {
    // add all buttons and their commentable item data to the redux store
    const commentButtons = Array.prototype.slice.call(document.querySelectorAll(`[${Commenting.data.itemAttribute}]`));
    commentButtons.map(buttonElement => {
      const item = buttonElement.getAttribute(`${Commenting.data.itemAttribute}`);
      const permalink = buttonElement.getAttribute(`${Commenting.data.permalinkAttribute}`);
      const disqusPermalink = buttonElement.getAttribute(`${Commenting.data.disqusPermalinkAttribute}`);
      const target = buttonElement.getAttribute(`${Commenting.data.targetAttribute}`);
      const disqusContainerElement = document.querySelector(`${target}`);
      const count = buttonElement.getAttribute(`${Commenting.data.countAttribute}`);

      // this is our expected commenter object
      const commenter = {
        item,
        permalink,
        disqusPermalink,
        buttonElement,
        disqusContainerElement,
        count,
        currentThread: false
      };
      return Commenting.data.store.dispatch(addCommenter(commenter));
    });
  },

  listenForButtonClick() {
    const { commenters } = Commenting.data.store.getState().commentingReducer;
    commenters.map(commenter => {
      const clickable = commenter.buttonElement;
      const createListenerEvent = () => {
        clickable.onclick = () => {
          Commenting.handleClick(clickable);
        };
      };
      return createListenerEvent();
    });
  },

  handleClick(clickedElement) {
    const item = clickedElement.getAttribute(`${Commenting.data.itemAttribute}`);
    Commenting.data.store.dispatch(setCurrentThread(item));
  },

  listenForStateChange() {
    Commenting.data.store.subscribe(() => {
      // do some user feedback
      const { commenters } = Commenting.data.store.getState().commentingReducer;
      commenters.map(commenter => {
        return Commenting.updateComponent(commenter);
      });
    });
  },

  updateComponent(commenter) {
    if (commenter.currentThread) {
      Commenting.moveThread(commenter);
    }

    Commenting.updateButtonStyles(commenter.buttonElement, commenter.currentThread);
    commenter.buttonElement.setAttribute(Commenting.data.countAttribute, commenter.count);
  },

  moveThread(commenter) {
    let thread = document.getElementById(Commenting.data.disqusId);

    // create a thread if there isn't one
    if (!thread) {
      thread = document.createElement("div");
      thread.setAttribute("id", Commenting.data.disqusId);
    }

    // move the thread to the container and then get the comments, unless it's already there
    if (!commenter.disqusContainerElement.querySelector(`#${Commenting.data.disqusId}`)) {
      commenter.disqusContainerElement.appendChild(thread);
      Commenting.data.store.dispatch(fetchDisqus(commenter));
      Commenting.handleOpen(commenter.disqusContainerElement);
    }
  },

  handleOpen(disqusContainerElement) {
    LoadIntersectionObserver()
      .then(() => {
        // Observe the parent container because the comments may have display: none
        Commenting.createObserver(disqusContainerElement.parentElement);
      })
      .catch(() => {
        // Something broke, just scroll anyway
        Commenting.scrollIntoView(disqusContainerElement.parentElement);
      });
  },

  updateButtonStyles(buttonElement, currentThread) {
    // Animate the button, but just once
    const resetAnimation = () => {
      requestAnimationFrame(() => {
        buttonElement.classList.remove(`${Commenting.data.animationClass}`);
      });
    };

    const hasClass = buttonElement.classList.contains(`${Commenting.data.activeClass}`);
    if (currentThread && !hasClass) {
      requestAnimationFrame(() => {
        buttonElement.classList.add(`${Commenting.data.activeClass}`);
        buttonElement.classList.add(`${Commenting.data.animationClass}`);
      });
      buttonElement.addEventListener("animationend", resetAnimation, { once: true });
    } else if (!currentThread && hasClass) {
      requestAnimationFrame(() => {
        buttonElement.classList.remove(`${Commenting.data.activeClass}`);
        buttonElement.classList.add(`${Commenting.data.animationClass}`);
      });
      buttonElement.addEventListener("animationend", resetAnimation, { once: true });
    }
  },

  createObserver(elementToObserve) {
    const config = {
      rootMargin: "50px 0px 50px 0px",
      threshold: 0.2
    };

    const observer = new IntersectionObserver(Commenting.handleIntersect, config);
    observer.observe(elementToObserve);
  },

  handleIntersect(entries, observer) {
    Array.prototype.forEach.call(entries, entry => {
      if (entry.isIntersecting) {
        // Stop watching, our work here is finished
        observer.unobserve(entry.target);
      } else {
        // Can't see the comments, time to scroll and stop watching
        Commenting.scrollToComments(entry.target);
        observer.unobserve(entry.target);
      }
    });
  },

  scrollToComments(target) {
    const navBuffer = -75;
    return SmoothScrollToTarget(target, navBuffer);
  }
};
export default Commenting;
